/**
 * Shared scrollable layout for profile edit screens.
 * Navigation header and back button are provided by the stack navigator.
 * On native, an optional footer stays pinned below the scroll area (not on web)
 * and rides the keyboard via KeyboardStickyView.
 */
import { useRef, useState } from "react";
import {
    Platform,
    ScrollView,
    View,
    type LayoutChangeEvent,
    type ScrollViewProps,
} from "react-native";
import {
    KeyboardAwareScrollView,
    KeyboardStickyView,
    type KeyboardAwareScrollViewRef,
} from "react-native-keyboard-controller";
import {
    SafeAreaView,
    useSafeAreaInsets,
} from "react-native-safe-area-context";

import { Text } from "@/components/ui/text";

type ProfileScreenLayoutProps = {
    description?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    contentContainerClassName?: ScrollViewProps["contentContainerClassName"];
};

/** Gap between the focused input and the sticky footer / keyboard. */
const KEYBOARD_BOTTOM_OFFSET = 24;

/**
 * Tall enough to cover the gap under the translated footer while the keyboard
 * is open (KeyboardStickyView only moves the view; it does not fill below it).
 */
const FOOTER_KEYBOARD_FILL_HEIGHT = 400;

export function ProfileScreenLayout({
    description,
    children,
    footer,
    contentContainerClassName,
}: ProfileScreenLayoutProps) {
    const stickyFooter = Platform.OS !== "web" && footer != null;
    const insets = useSafeAreaInsets();
    const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
    const [footerHeight, setFooterHeight] = useState(0);
    const effectiveFooterHeight = stickyFooter ? footerHeight : 0;

    function handleFooterLayout(event: LayoutChangeEvent) {
        const nextHeight = event.nativeEvent.layout.height;
        if (nextHeight === footerHeight) {
            return;
        }

        // Footer appeared or grew (e.g. Save bar while keyboard is open) —
        // re-check that the focused input stays above footer + keyboard.
        if (nextHeight > footerHeight) {
            requestAnimationFrame(() => {
                scrollRef.current?.assureFocusedInputVisible();
            });
        }

        setFooterHeight(nextHeight);
    }

    const contentContainerClass =
        contentContainerClassName ??
        "gap-6 px-4 pb-6 pt-2 web:mx-auto web:w-full web:max-w-lg";

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            {/*
              Keep ScrollView + footer as flex siblings (not absolute).
              Absolute footer made ScrollView full-height under the Save bar,
              so scroll-to-focused-input aimed too low.
            */}
            {Platform.OS === "web" ? (
                <ScrollView
                    className="flex-1"
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="interactive"
                    contentContainerClassName={contentContainerClass}
                >
                    {description ? (
                        <Text variant="caption">{description}</Text>
                    ) : null}
                    <View>{children}</View>
                    {footer}
                </ScrollView>
            ) : (
                <>
                    <KeyboardAwareScrollView
                        ref={scrollRef}
                        className="flex-1"
                        keyboardShouldPersistTaps="handled"
                        keyboardDismissMode="interactive"
                        bottomOffset={
                            effectiveFooterHeight + KEYBOARD_BOTTOM_OFFSET
                        }
                        extraKeyboardSpace={effectiveFooterHeight}
                        contentContainerClassName={contentContainerClass}
                    >
                        {description ? (
                            <Text variant="caption">{description}</Text>
                        ) : null}
                        <View>{children}</View>
                    </KeyboardAwareScrollView>
                    {stickyFooter ? (
                        <KeyboardStickyView
                            // SafeArea bottom inset lifts the resting footer; compensate
                            // when open so the bar sits flush on the keyboard.
                            offset={{ closed: 0, opened: insets.bottom }}
                        >
                            <View
                                className="border-t border-border bg-background px-4 py-3"
                                onLayout={handleFooterLayout}
                            >
                                {footer}
                            </View>
                            {/*
                              KeyboardStickyView only translates — paint below the
                              footer so ScrollView does not show through above the
                              keyboard. Negative margin keeps layout height = footer
                              (does not steal space from the ScrollView).
                            */}
                            <View
                                pointerEvents="none"
                                className="bg-background"
                                style={{
                                    height: FOOTER_KEYBOARD_FILL_HEIGHT,
                                    marginBottom: -FOOTER_KEYBOARD_FILL_HEIGHT,
                                }}
                            />
                        </KeyboardStickyView>
                    ) : null}
                </>
            )}
        </SafeAreaView>
    );
}
